import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Job, JobStatus } from '@core/generated/gql-types';
import { useClientStore } from '@core/client/useClientStore';
import { useJobStore } from '@core/job/useJobStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { SortDirection, useJobsTable } from '@/modules/jobs/hooks/useJobsTable';
import { SortField } from '@/modules/jobs/hooks/useJobsTable';
import JobDrawer from '@/modules/jobs/components/JobDrawer';
import useFeatureView from '@/hooks/useFeatureView';
import { getJobDrawerView } from '@/modules/jobs/views/jobDrawer.view';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase } from '@fortawesome/pro-regular-svg-icons';
import { getJobApplicationCount } from '@/modules/jobs/utils/jobFilters.utils';

interface UnfilledJobsTableProps {
  allFilteredJobs: Job[];
  timeframeStartDate: Dayjs | undefined;
  timeframeEndDate: Dayjs | undefined;
  isLoading: boolean;
}

const formatStartDate = (startDate: string) => {
  const date = dayjs(parseInt(startDate));
  if (date.isSame(dayjs(), 'day')) return 'Today';
  if (date.isSame(dayjs().add(1, 'day'), 'day')) return 'Tomorrow';
  return date.format('D MMM YYYY');
};

const UnfilledJobsTable = ({
  allFilteredJobs,
  timeframeStartDate,
  timeframeEndDate,
  isLoading,
}: UnfilledJobsTableProps) => {
  const { getClientById } = useClientStore();
  const { getJobById } = useJobStore();
  const drawerView = useFeatureView(getJobDrawerView);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { getTerm } = useTemplateStore();

  const {
    setSortField,
    setSortDirection,
    setStatusFilters,
    paginatedFilteredJobs,
    pagination: jobsPagination,
  } = useJobsTable(allFilteredJobs, timeframeStartDate, timeframeEndDate);

  useEffect(() => {
    setStatusFilters([JobStatus.Unfilled]);
    setSortField('startDate' as SortField);
    setSortDirection('asc' as SortDirection);
  }, [setStatusFilters, setSortField, setSortDirection]);

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedJobId(null);
  };

  return (
    <div className="flex h-[500px] flex-col rounded-xl bg-white p-4 shadow">
      <div className="text-md mb-2 h-6 font-semibold text-blue-900">
        Unfilled Jobs{' '}
        <span className="text-sm">({jobsPagination.totalItems})</span>
      </div>
      {paginatedFilteredJobs.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="mb-6 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faBriefcase}
              className="text-gray-400"
              style={{ fontSize: '3rem' }}
            />
          </div>
          <p className="mb-2 text-lg font-semibold text-gray-900">
            All jobs are filled!
          </p>
          <p className="max-w-sm text-center text-sm text-gray-500">
            You're all set. Time to chill... or create a new job.
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 border-b border-gray-200 bg-white">
                <tr className="border-t border-gray-200 text-xs text-gray-500">
                  <th className="py-2 text-left font-medium">Job title</th>
                  <th className="py-2 text-left font-medium">
                    {getTerm(TemplateTerm.CLIENT, false)}
                  </th>
                  <th className="py-2 text-left font-medium">Start date</th>
                  <th className="py-2 text-left font-medium">Applicants</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFilteredJobs.map((job) => {
                  const applicationCount = getJobApplicationCount(job);
                  return (
                    <tr
                      key={job.id}
                      onClick={() => handleJobClick(job.id)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="items-left flex gap-2 py-2">
                        <span className="text-xs font-semibold">
                          {job.title}
                        </span>
                      </td>
                      <td className="py-2 text-left text-xs">
                        {getClientById(job.clientId)?.name || job.clientId}
                      </td>
                      <td className="py-2 text-left text-xs">
                        {job.startDate ? formatStartDate(job.startDate) : '-'}
                      </td>
                      <td className="py-2 text-left text-xs">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            applicationCount === 0
                              ? 'bg-red-100 text-red-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {applicationCount}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-2 flex shrink-0 items-center justify-between border-t border-gray-100 pt-2">
            <div className="text-sm text-gray-700">
              Showing {jobsPagination.endIndex ? jobsPagination.startIndex : 0}{' '}
              to {jobsPagination.endIndex} of {jobsPagination.totalItems}{' '}
              results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  jobsPagination.onPageChange(jobsPagination.currentPage - 1)
                }
                disabled={jobsPagination.currentPage === 1 || isLoading}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => {
                  jobsPagination.onPageChange(jobsPagination.currentPage + 1);
                }}
                disabled={
                  (jobsPagination.currentPage >= jobsPagination.totalPages &&
                    !jobsPagination.hasMore) ||
                  isLoading
                }
                className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <JobDrawer
        job={selectedJobId ? getJobById(selectedJobId) : null}
        isOpen={isDrawerOpen}
        view={drawerView}
        onClose={handleCloseDrawer}
      />
    </div>
  );
};

export default UnfilledJobsTable;
